import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uploadStatusActions } from '@constants/actionTypes';
import { useUserUploadedDocs } from '@hooks/features/useDocs';
import { useUploadStatus } from '@hooks/features/useUploadStatus';
import { DocumentVaultFilterContext } from '@/contexts/DocumentVaultFilterContext';
import {
  buildDocumentStatusLookup,
  collectFacetOptions,
  countNewlyProcessedDocs,
  filterDocuments,
  sortDocuments,
} from '@utils/documentVault';

/*
  Filtering runs client-side: fetchUserDocs only forwards doc_types to the backend today, and a
  single user's library is small enough that filtering in memory keeps every facet instant.
*/
export function useVaultDocuments() {
  const dispatch = useDispatch();
  const { filters } = React.useContext(DocumentVaultFilterContext);

  const onAfterDocsReceived = React.useCallback(() => {
    dispatch({ type: uploadStatusActions.clearNewlyCompletedUploadDocsCount });
  }, [dispatch]);

  const { docs, isLoading, onRefreshDocs } = useUserUploadedDocs(null, onAfterDocsReceived);

  // useUserUploadedDocs reports a failed fetch as a null doc list rather than an error, so a
  // settled null is the only signal that the request came back empty-handed.
  const hasError = !isLoading && docs === null;

  // Polls the upload-status feed, the only source of per-document processing state and of the
  // newly-processed count. Without it both would sit frozen at whatever the store held on arrival.
  useUploadStatus();
  const uploadStatusData = useSelector((state) => state.uploadStatus?.data);

  // There is no processing status on the document record; the upload-status feed carries it for
  // recent jobs, and anything it doesn't mention finished processing long ago.
  const statusLookup = React.useMemo(() => buildDocumentStatusLookup(uploadStatusData), [uploadStatusData]);

  const facetOptions = React.useMemo(() => collectFacetOptions(docs, statusLookup), [docs, statusLookup]);

  const newlyProcessedCount = React.useMemo(
    () => countNewlyProcessedDocs(uploadStatusData, docs),
    [uploadStatusData, docs]
  );

  const filteredDocs = React.useMemo(
    () => sortDocuments(filterDocuments(docs, filters, statusLookup), filters.sort),
    [docs, filters, statusLookup]
  );

  const totalCount = docs?.length || 0;
  const filteredCount = filteredDocs.length;
  const pageCount = Math.max(1, Math.ceil(filteredCount / filters.pageSize));
  // A filter change resets the page, but a doc list that shrinks underneath a deep page (a
  // refresh, a newly-applied narrower search) would otherwise strand the user on an empty page.
  const page = Math.min(filters.page, pageCount);

  const pagedDocs = React.useMemo(() => {
    const start = (page - 1) * filters.pageSize;
    return filteredDocs.slice(start, start + filters.pageSize);
  }, [filteredDocs, page, filters.pageSize]);

  return {
    docs: pagedDocs,
    filteredDocs,
    allDocs: docs,
    facetOptions,
    statusLookup,
    totalCount,
    filteredCount,
    page,
    pageCount,
    isLoading,
    hasError,
    newlyProcessedCount,
    onRefreshDocs,
  };
}
